import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import './CatalogModal.css';

const VERTICAL_OPTIONS = [
  { value: 'commerce', label: 'Commerce' },
  { value: 'services', label: 'Services' },
  { value: 'restaurants', label: 'Restaurants' },
];

const TRANSFORMATION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'fit_center', label: 'Fit Center' },
  { value: 'crop_center', label: 'Crop Center' },
];

const DEFAULT_FORM = {
  vertical: 'commerce',
  name: '',
  description: '',
  product_count: 10,
  feed_count: 1,
  default_image_url: '',
  fallback_image_url: [''],
  is_catalog_segment: false,
  da_display_settings: {
    carousel_ad: { transformation_type: 'none' },
    single_ad: { transformation_type: 'none' },
  },
};

const CatalogModal = ({ isOpen, onClose, onConfirm, editingCatalog }) => {
  const isEdit = Boolean(editingCatalog);

  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [isLoading, setIsLoading] = useState(false);

  // Pre-populate form when editing
  useEffect(() => {
    if (isOpen) {
      if (editingCatalog) {
        setFormData({
          vertical: editingCatalog.vertical || 'commerce',
          name: editingCatalog.name || '',
          description: editingCatalog.description || '',
          product_count: editingCatalog.productCount || 0,
          feed_count: editingCatalog.feedCount || 1,
          default_image_url: editingCatalog.defaultImageUrl || '',
          fallback_image_url:
            editingCatalog.fallbackImageUrls?.length > 0
              ? editingCatalog.fallbackImageUrls
              : [''],
          is_catalog_segment: editingCatalog.isCatalogSegment || false,
          da_display_settings: {
            carousel_ad: {
              transformation_type:
                editingCatalog.daDisplaySettings?.carouselAd?.transformationType || 'none',
            },
            single_ad: {
              transformation_type:
                editingCatalog.daDisplaySettings?.singleAd?.transformationType || 'none',
            },
          },
        });
      } else {
        setFormData(DEFAULT_FORM);
      }
    }
  }, [isOpen, editingCatalog]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageUrlChange = (e, index) => {
    const newUrls = [...formData.fallback_image_url];
    newUrls[index] = e.target.value;
    setFormData((prev) => ({ ...prev, fallback_image_url: newUrls }));
  };

  const handleAddImageUrl = () => {
    setFormData((prev) => ({
      ...prev,
      fallback_image_url: [...prev.fallback_image_url, ''],
    }));
  };

  const handleRemoveImageUrl = (index) => {
    setFormData((prev) => ({
      ...prev,
      fallback_image_url: prev.fallback_image_url.filter((_, i) => i !== index),
    }));
  };

  const handleDisplaySettingChange = (setting, transformationType) => {
    setFormData((prev) => ({
      ...prev,
      da_display_settings: {
        ...prev.da_display_settings,
        [setting]: { transformation_type: transformationType },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error('Catalog name is required', { position: 'bottom-right' });
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(formData);
      handleClose();
    } catch (error) {
      console.error('CatalogModal submit error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData(DEFAULT_FORM);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="catalog-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="catalog-modal-header">
          <h2>{isEdit ? 'Edit Catalog' : 'Create New Catalog'}</h2>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="catalog-modal-body">
          {/* Vertical & Name Row */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Vertical *</label>
              <select
                name="vertical"
                value={formData.vertical}
                onChange={handleChange}
                className="form-select"
              >
                {VERTICAL_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Catalog Name *</label>
              <input
                type="text"
                name="name"
                placeholder="e.g., my-new-catalogue"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <input
              type="text"
              name="description"
              placeholder="Short description of this catalog"
              value={formData.description}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          {/* Product & Feed Count Row */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Product Count</label>
              <input
                type="number"
                name="product_count"
                min="0"
                value={formData.product_count}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Feed Count</label>
              <input
                type="number"
                name="feed_count"
                min="1"
                value={formData.feed_count}
                onChange={handleChange}
                className="form-input"
              />
            </div>
          </div>

          {/* Default Image URL */}
          <div className="form-group">
            <label className="form-label">Default Image URL</label>
            <input
              type="url"
              name="default_image_url"
              placeholder="https://..."
              value={formData.default_image_url}
              onChange={handleChange}
              className="form-input"
            />
            {formData.default_image_url && (
              <div className="image-preview">
                <img
                  src={formData.default_image_url}
                  alt="Default"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            )}
          </div>

          {/* Fallback Image URLs */}
          <div className="form-group">
            <label className="form-label">Fallback Image URLs</label>
            {formData.fallback_image_url.map((url, index) => (
              <div key={index} className="image-url-row">
                <input
                  type="url"
                  placeholder="https://..."
                  value={url}
                  onChange={(e) => handleImageUrlChange(e, index)}
                  className="form-input"
                />
                {formData.fallback_image_url.length > 1 && (
                  <button
                    type="button"
                    className="btn-remove"
                    onClick={() => handleRemoveImageUrl(index)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            <button type="button" className="btn-add-image" onClick={handleAddImageUrl}>
              + Add Image URL
            </button>
          </div>

          {/* Display Settings */}
          <div className="form-section">
            <h3>Display Settings</h3>

            <div className="form-group">
              <label className="form-label">Carousel Ad - Transformation Type</label>
              <select
                value={formData.da_display_settings.carousel_ad.transformation_type}
                onChange={(e) => handleDisplaySettingChange('carousel_ad', e.target.value)}
                className="form-select"
              >
                {TRANSFORMATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Single Ad - Transformation Type</label>
              <select
                value={formData.da_display_settings.single_ad.transformation_type}
                onChange={(e) => handleDisplaySettingChange('single_ad', e.target.value)}
                className="form-select"
              >
                {TRANSFORMATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Checkbox */}
          <div className="form-group checkbox-group">
            <input
              type="checkbox"
              id="is_catalog_segment"
              name="is_catalog_segment"
              checked={formData.is_catalog_segment}
              onChange={handleChange}
              className="form-checkbox"
            />
            <label htmlFor="is_catalog_segment" className="checkbox-label">
              This is a catalog segment
            </label>
          </div>

          {/* Action Buttons */}
          <div className="catalog-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-create" disabled={isLoading}>
              {isLoading
                ? isEdit ? '⏳ Saving...' : '⏳ Creating...'
                : isEdit ? '✓ Save Changes' : '✓ Create Catalog'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CatalogModal;
